import { Avatar, Badge, Card, Group, Stack, Text, useMantineColorScheme, useMantineTheme, Title } from "@mantine/core";

export interface TeamProfileCardProps {
  /** URL for the team logo image */
  logoUrl?: string;
  /** Team display name */
  teamName: string;
  /** Parent franchise display name */
  franchise?: string;
  /** Current competitive tier (e.g., T1, T2, Academy) */
  tier?: string;
  /** Compact layout for list/sidebar contexts */
  compact?: boolean;
  /** If true, Card will stretch to fill parent height */
  fullHeight?: boolean;
}

/**
 * TeamProfileCard
 *
 * Reusable, theme-aware card that displays core team information. Supports a
 * compact layout for use in lists or sidebars and a full layout for profile pages.
 *
 * The badge colors and backgrounds inherit the Mantine theme. To change brand or
 * badge colors globally, edit tokens in src/theme.ts (e.g., primaryColor or
 * other.franchiseColor).
 */
export default function TeamProfileCard({ logoUrl, teamName, franchise, tier, compact = false, fullHeight = false }: TeamProfileCardProps) {
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';

  const franchiseBadgeProps: { variant: 'filled' | 'light'; color?: string } = {
    // Use filled in dark for better contrast; light in light mode.
    // To change this globally, see src/theme.ts -> other.franchiseColor
    variant: isDark ? 'filled' : 'light',
    color: (theme.other as any)?.franchiseColor ?? theme.primaryColor,
  };

  const tierBadgeProps: { variant: 'filled' | 'light'; color?: string } = {
    variant: isDark ? 'filled' : 'light',
    color: theme.primaryColor,
  };

  if (compact) {
    return (
      <Card withBorder radius="md" p="sm" style={{ height: fullHeight ? '100%' : undefined, width: '100%' }}>
        <Group gap="sm" wrap="nowrap" align="center">
          <Avatar src={logoUrl} size={48} radius="md" alt={`${teamName} logo`} />
          <Stack gap={2} style={{ flex: 1 }}>
            <Text fw={700} size="sm" lineClamp={1}>
              {teamName}
            </Text>
            <Group gap={6} wrap="wrap">
              {franchise && (
                <Badge size="xs" {...franchiseBadgeProps}>
                  {franchise}
                </Badge>
              )}
              {tier && (
                <Badge size="xs" {...tierBadgeProps}>
                  {tier}
                </Badge>
              )}
            </Group>
          </Stack>
        </Group>
      </Card>
    );
  }

  return (
    <Card withBorder radius="md" p="md" style={{ height: fullHeight ? '100%' : undefined, width: '100%', display: 'flex' }}>
      <Group align="flex-start" wrap="nowrap" style={{ flex: 1, minHeight: 0 }}>
        <Avatar src={logoUrl} size={96} radius="md" alt={`${teamName} logo`} />
        <Stack gap={6} style={{ flex: 1, minHeight: 0 }}>
          {/* Title color is scheme-aware for stronger contrast. Change globally in src/theme.ts via appThemes. */}
          <Title order={3} c={isDark ? 'gray.0' : 'dark.7'}>
            {teamName}
          </Title>
          <Group gap={8} wrap="wrap">
            {franchise && (
              <Badge {...franchiseBadgeProps}>Franchise: {franchise}</Badge>
            )}
            {tier && <Badge {...tierBadgeProps}>Tier: {tier}</Badge>}
          </Group>
        </Stack>
      </Group>
    </Card>
  );
}
